import { useCallback } from 'react';
import PolicyForm, { PolicyFormProps } from './PolicyForm';
import useCrud from 'hooks/useCrud';
import { ConfigResponse, config, policy as policyApi, updatePolicy } from './service';
import { useMount } from 'ahooks';
import { Button, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { LoadingOutlined } from '@ant-design/icons';
import {
  getMustacheExecuteContextV2,
  getSlateExecuteContext,
} from 'views/Aide/Scenario/PolicyForm';
import { useNonPlatformQaEnable } from 'hooks/business/useOrgConfig';
import styled from '@emotion/styled';

export interface PolicyProps extends Pick<PolicyFormProps, 'platform' | 'region'> {
  initialValue?: [ConfigResponse, NightWatch.Policy];
  storeGroupId: string | number;
  policyId: string | number;
  editable?: boolean;
  onClose: () => void;
}

function Policy(props: PolicyProps) {
  const {
    initialValue,
    platform: platformProp,
    region,
    storeGroupId,
    policyId,
    editable,
    onClose,
  } = props;

  const { t } = useTranslate();

  const nonPlatformQaEnable = useNonPlatformQaEnable();

  const platform = nonPlatformQaEnable ? undefined : platformProp;

  const {
    item = initialValue,
    loading,
    fetch,
  } = useCrud({
    read: useCallback(() => {
      return Promise.all([
        config({ groupId: storeGroupId, needPolicy: true }),
        policyApi(policyId),
      ]);
    }, [storeGroupId, policyId]),
  });

  useMount(() => {
    if (!item) {
      fetch().catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      });
    }
  });

  if (!item) {
    return loading ? <LoadingOutlined /> : null;
  }

  const [{ policy: policyList }, policy] = item;

  return (
    <PolicyForm
      platform={platform}
      region={region}
      editable={editable}
      initialValues={{
        ...policy,
        replyContent: policy.replyContent.map((content) => {
          return getSlateExecuteContext({
            content,
            translatedContent: '',
          });
        }),
      }}
      policyList={policyList}
      onSubmit={(values) => {
        const { fallbackPolicyId, priority, replyContent: slateReplyContent, ...others } = values;

        if (!fallbackPolicyId || !priority) {
          return Promise.reject();
        }

        const replyContent = slateReplyContent
          .map(getMustacheExecuteContextV2)
          .map((_) => _.content);

        return updatePolicy({
          fallbackPolicyId,
          priority,
          replyContent,
          ...others,
        })
          .then(() => {
            onClose();

            message.success(t('operation.saveSucceed'));
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
          });
      }}
    >
      {(form, props) => {
        const { isSubmitting, handleSubmit } = props;

        return (
          <Container>
            {form}
            <footer>
              <Button type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                {t('marketing.保存')}
              </Button>
              <Button onClick={onClose} style={{ marginLeft: '12px' }}>
                {t('translation.取消')}
              </Button>
            </footer>
          </Container>
        );
      }}
    </PolicyForm>
  );
}

const Container = styled.div`
  > footer {
    display: flex;
    align-items: center;
    position: sticky;
    bottom: -16px;
    z-index: 1;
    height: 56px;
    padding: 0 24px;
    background: var(--fett-bg-panel);
    border-top: solid 1px var(--fett-border-default);
  }
`;

export default Policy;
